import { useState, useEffect } from 'react';
import { Calendar, Music, Trophy, HelpCircle, Tv, Clock, MapPin, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  tapsCloseup: "https://customer-assets.emergentagent.com/job_35f581b7-c0c0-4500-8d03-2f1ebfa0c512/artifacts/oxrr7pam_B6EDE2C5-72F3-4451-8901-19945C8107AD.jpeg",
  comedyNight: "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/kxwbw9dy_IMG_5681.heic",
};

// Featured Comedy Event
const COMEDY_EVENT = {
  title: "Dark Hearts English Comedy",
  performer: "Tera Kilbride",
  instagram: "@teracomedy_",
  date: "Friday, August 14",
  time: "21:00",
  description: "Comedy + Drinks at the pub! Outdoor seating before the show. Join us for a night of laughs with English stand-up comedy.",
  schedule: "Every 2nd week of the month",
};

// Sports categories with icons/flags
const sportsCategories = [
  { id: 'rugby', name: 'Rugby', icon: '🏉', description: 'World Cup, Pro14' },
  { id: 'football', name: 'Football', icon: '⚽', description: 'Swiss Super League, Euro Cup, Champions League' },
  { id: 'swiss', name: 'Swiss Sports', icon: '🇨🇭', description: 'Ice Hockey, Ski Racing' },
  { id: 'golf', name: 'Golf', icon: '⛳', description: 'The Masters, Ryder Cup, PGA Tour' },
];

const upcomingEvents = [
  {
    id: 3,
    type: 'music',
    title: 'Live Music',
    subtitle: 'Traditional Irish Session',
    date: 'Thu, Jul 30',
    time: '20:00',
    featured: true,
  },
  {
    id: 4,
    type: 'quiz',
    title: 'Quiz Night',
    subtitle: 'Test your knowledge!',
    date: 'Sat, Aug 15',
    time: '19:30',
    featured: true,
  },
  {
    id: 5,
    type: 'quiz',
    title: 'Quiz Night',
    subtitle: 'Test your knowledge!',
    date: 'Fri, Aug 28',
    time: '19:30',
    featured: false,
  },
  {
    id: 6,
    type: 'karaoke',
    title: 'Karaoke Night',
    subtitle: 'Social circle event',
    date: 'Wed, Aug 6',
    time: '21:00',
    featured: true,
  },
];

// Live Sports Fixtures Component
const LiveSportsFixtures = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch fixtures from backend
    const fetchFixtures = async () => {
      try {
        const response = await axios.get(`${API}/sports/fixtures`);
        setFixtures(response.data.fixtures || []);
      } catch {
        // Fallback fixtures if API fails
        setFixtures([
          { id: 1, league: 'Premier League', home: 'Arsenal', away: 'Chelsea', date: '2026-02-01', time: '15:00', sport: 'football' },
          { id: 2, league: 'Premier League', home: 'Liverpool', away: 'Man United', date: '2026-02-01', time: '17:30', sport: 'football' },
          { id: 3, league: 'Six Nations', home: 'Ireland', away: 'France', date: '2026-02-01', time: '14:15', sport: 'rugby' },
          { id: 4, league: 'Six Nations', home: 'England', away: 'Wales', date: '2026-02-01', time: '16:45', sport: 'rugby' },
          { id: 5, league: 'Swiss National League', home: 'SC Bern', away: 'ZSC Lions', date: '2026-02-02', time: '19:45', sport: 'hockey' },
          { id: 6, league: 'Champions League', home: 'Real Madrid', away: 'Bayern Munich', date: '2026-02-04', time: '21:00', sport: 'football' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchFixtures();
  }, []);

  const getSportIcon = (sport) => {
    switch(sport) {
      case 'football': return '⚽';
      case 'rugby': return '🏉';
      case 'hockey': return '🏒';
      default: return '🏆';
    }
  };

  const getSportColor = (sport) => {
    switch(sport) {
      case 'football': return 'bg-green-500/10 border-green-500/30';
      case 'rugby': return 'bg-orange-500/10 border-orange-500/30';
      case 'hockey': return 'bg-blue-500/10 border-blue-500/30';
      default: return 'bg-[#D4AF37]/10 border-[#D4AF37]/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {fixtures.map((fixture) => (
        <div 
          key={fixture.id}
          className={`border rounded-sm p-5 hover:border-[#D4AF37]/50 transition-all ${getSportColor(fixture.sport)}`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl">{getSportIcon(fixture.sport)}</span>
            <span className="text-xs font-['Manrope'] uppercase tracking-wider text-[#A3A3A3]">
              {fixture.league}
            </span>
          </div>
          <div className="text-center mb-3">
            <p className="text-[#E8E6D9] font-['Playfair_Display'] text-lg font-semibold">
              {fixture.home}
            </p>
            <p className="text-[#D4AF37] text-sm font-['Manrope'] my-1">vs</p>
            <p className="text-[#E8E6D9] font-['Playfair_Display'] text-lg font-semibold">
              {fixture.away}
            </p>
          </div>
          <div className="flex items-center justify-center gap-4 text-sm font-['Manrope'] border-t border-white/10 pt-3">
            <div className="flex items-center gap-1.5 text-[#A3A3A3]">
              <Calendar size={14} />
              <span>{new Date(fixture.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#D4AF37]">
              <Clock size={14} />
              <span>{fixture.time}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const regularEvents = [
  {
    icon: <Music size={32} className="text-[#D4AF37]" />,
    title: 'Live Music',
    description: 'Enjoy live traditional Irish music sessions with local and visiting musicians. Authentic jigs, reels, and ballads.',
    schedule: 'Thursday, July 30 • 20:00',
    highlight: 'Free Entry',
  },
  {
    icon: <HelpCircle size={32} className="text-[#D4AF37]" />,
    title: 'Quiz Night',
    description: 'Put your knowledge to the test! Gather your team and compete for prizes. General knowledge, sports, music, and Irish trivia rounds.',
    schedule: 'Sat, Aug 15 & Fri, Aug 28 • 19:30',
    highlight: 'Win Prizes',
  },
  {
    icon: <span className="text-3xl">🎤</span>,
    title: 'Karaoke Night',
    description: 'Grab the mic and show off your singing skills! A fun social circle event where everyone can join in. All skill levels welcome!',
    schedule: 'Every Thursday • 21:00',
    highlight: 'Social Circle',
  },
  {
    icon: <span className="text-3xl">😂</span>,
    title: 'Dark Hearts Comedy',
    description: 'English stand-up comedy night featuring top comedians. Comedy + drinks at the pub with outdoor seating before the show!',
    schedule: '2nd Week of Month • 21:00',
    highlight: 'Live Comedy',
  },
  {
    icon: <Tv size={32} className="text-[#D4AF37]" />,
    title: 'Live Sports Daily',
    description: 'Multiple screens showing the best live sports. Swiss Super League matches live every weekend! Plus rugby, golf, and more.',
    schedule: 'Swiss Super League • Every Weekend',
    highlight: 'Big Screens',
  },
];

const EventCard = ({ event }) => {
  const getEventIcon = () => {
    if (event.type === 'music') return <Music size={20} />;
    if (event.type === 'quiz') return <HelpCircle size={20} />;
    if (event.type === 'karaoke') return <span className="text-xl">🎤</span>;
    const sport = sportsCategories.find(s => s.id === event.sport);
    return <span className="text-xl">{sport?.icon || '🏆'}</span>;
  };

  const getEventColor = () => {
    if (event.type === 'music') return 'border-purple-500/30 bg-purple-500/5';
    if (event.type === 'quiz') return 'border-blue-500/30 bg-blue-500/5';
    if (event.type === 'karaoke') return 'border-pink-500/30 bg-pink-500/5';
    return 'border-[#D4AF37]/30 bg-[#D4AF37]/5';
  };

  return (
    <div 
      data-testid={`event-card-${event.id}`}
      className={`border rounded-sm p-5 hover:border-[#D4AF37]/50 transition-all ${event.featured ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10' : 'border-white/10 bg-[#050a09]'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getEventColor()}`}>
          {getEventIcon()}
        </div>
        {event.featured && (
          <span className="text-xs font-['Manrope'] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded-sm">
            Featured
          </span>
        )}
      </div>
      <h4 className="font-['Playfair_Display'] text-lg text-[#E8E6D9] mb-1">{event.title}</h4>
      <p className="text-[#A3A3A3] text-sm font-['Manrope'] mb-3">{event.subtitle}</p>
      <div className="flex items-center gap-4 text-sm font-['Manrope']">
        <div className="flex items-center gap-1.5 text-[#D4AF37]">
          <Calendar size={14} />
          <span>{event.date}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[#A3A3A3]">
          <Clock size={14} />
          <span>{event.time}</span>
        </div>
      </div>
    </div>
  );
};

const EventsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredEvents = activeFilter === 'all' 
    ? upcomingEvents 
    : upcomingEvents.filter(e => e.type === activeFilter || e.sport === activeFilter);

  return (
    <div data-testid="events-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#050a09]">
        <div className="absolute inset-0 opacity-20">
          <img
            src={ASSETS.tapsCloseup}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050a09] via-[#050a09]/80 to-[#050a09]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
            What&apos;s On
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#E8E6D9] mb-4">
            Events & <span className="text-[#D4AF37]">Live Sports</span>
          </h1>
          <p className="text-[#A3A3A3] font-['Manrope'] text-lg">
            Live music, quiz nights, and all the best sporting action
          </p>
        </div>
      </section>

      {/* Regular Events */}
      <section className="py-16 md:py-24 bg-[#0b1412]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
              Weekly Highlights
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#E8E6D9]">
              Regular <span className="text-[#D4AF37]">Events</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {regularEvents.map((event, index) => (
              <div 
                key={index}
                className="bg-[#050a09] border border-white/5 rounded-sm p-8 hover:border-[#D4AF37]/30 transition-all card-lift"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center mb-6">
                  {event.icon}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#E8E6D9]">
                    {event.title}
                  </h3>
                  <span className="text-xs font-['Manrope'] uppercase tracking-wider text-[#050a09] bg-[#D4AF37] px-2 py-0.5 rounded-sm">
                    {event.highlight}
                  </span>
                </div>
                <p className="text-[#A3A3A3] font-['Manrope'] text-sm leading-relaxed mb-4">
                  {event.description}
                </p>
                <p className="text-[#D4AF37] font-['Manrope'] text-sm font-semibold">
                  {event.schedule}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Comedy Night */}
      <section 
        data-testid="comedy-section"
        className="py-16 md:py-24 bg-[#050a09]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
              Featured Event
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#E8E6D9]">
              Comedy <span className="text-[#D4AF37]">Night</span>
            </h2>
          </div>

          <div className="bg-gradient-to-r from-[#0b1412] to-[#1a1a2e] border border-[#D4AF37]/30 rounded-sm overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Event Details */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">🎤</span>
                  <span className="text-xs font-['Manrope'] uppercase tracking-wider text-[#050a09] bg-[#D4AF37] px-3 py-1 rounded-sm font-bold">
                    Next Show
                  </span>
                </div>
                <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#D4AF37] mb-2">
                  {COMEDY_EVENT.title}
                </h3>
                <p className="text-[#E8E6D9] font-['Manrope'] text-lg mb-6">
                  {COMEDY_EVENT.description}
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                      <span className="text-xl">⭐</span>
                    </div>
                    <div>
                      <p className="text-[#E8E6D9] font-['Playfair_Display'] text-xl font-semibold">{COMEDY_EVENT.performer}</p>
                      <a 
                        href="https://instagram.com/teracomedy_" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#D4AF37] font-['Manrope'] text-sm hover:underline"
                      >
                        {COMEDY_EVENT.instagram}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                      <Calendar size={20} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-[#E8E6D9] font-['Manrope'] font-semibold">{COMEDY_EVENT.date}</p>
                      <p className="text-[#A3A3A3] font-['Manrope'] text-sm">{COMEDY_EVENT.time} • Doors open 20:30</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                      <Clock size={20} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-[#A3A3A3] font-['Manrope'] text-sm">{COMEDY_EVENT.schedule}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/reservations"
                    className="bg-[#D4AF37] text-[#050a09] px-8 py-3 rounded-sm font-bold text-sm tracking-wider uppercase hover:bg-[#E5C568] transition-all duration-300 btn-glow"
                  >
                    Reserve Seats
                  </Link>
                  <a
                    href="https://instagram.com/theshamrockbern"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-sm font-semibold text-sm tracking-wider uppercase hover:bg-[#D4AF37] hover:text-[#050a09] transition-all duration-300"
                  >
                    @theshamrockbern
                  </a>
                </div>
              </div>

              {/* Event Poster/Image */}
              <div className="relative bg-black flex items-center justify-center p-8 min-h-[400px]">
                <div className="text-center">
                  <p className="text-[#A3A3A3] font-['Manrope'] text-sm mb-2 uppercase tracking-wider">Comedy + Drinks at the Pub!!</p>
                  <h4 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">Dark Hearts</h4>
                  <h4 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#D4AF37] mb-4">English Comedy</h4>
                  <p className="text-white font-['Manrope'] text-xl font-bold mb-2">BERN</p>
                  <p className="text-[#D4AF37] font-['Manrope'] font-semibold">FRIDAY AUGUST 14 • 21:00</p>
                  <div className="mt-6 border-t border-white/20 pt-6">
                    <p className="text-white font-['Playfair_Display'] text-2xl font-bold">{COMEDY_EVENT.performer}</p>
                    <p className="text-[#A3A3A3] font-['Manrope'] text-sm">{COMEDY_EVENT.instagram}</p>
                  </div>
                  <p className="text-[#A3A3A3] font-['Manrope'] text-xs mt-4 uppercase">Outdoor seating before the show!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Sports Section */}
      <section className="py-16 md:py-24 bg-[#050a09]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
              On Our Screens
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#E8E6D9]">
              Live <span className="text-[#D4AF37]">Sports</span>
            </h2>
          </div>

          {/* Sports Categories */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {sportsCategories.map((sport) => (
              <div 
                key={sport.id}
                data-testid={`sport-category-${sport.id}`}
                className="bg-[#0b1412] border border-white/10 rounded-sm px-6 py-4 text-center hover:border-[#D4AF37]/30 transition-all"
              >
                <span className="text-3xl mb-2 block">{sport.icon}</span>
                <h4 className="font-['Manrope'] font-semibold text-[#E8E6D9] text-sm">{sport.name}</h4>
                <p className="text-[#525252] text-xs font-['Manrope'] mt-1">{sport.description}</p>
              </div>
            ))}
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { id: 'all', label: 'All Events' },
              { id: 'sports', label: 'Sports' },
              { id: 'music', label: 'Live Music' },
              { id: 'quiz', label: 'Quiz Night' },
            ].map((filter) => (
              <button
                key={filter.id}
                data-testid={`filter-${filter.id}`}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-5 py-2.5 rounded-sm font-['Manrope'] text-sm transition-all ${
                  activeFilter === filter.id
                    ? 'bg-[#D4AF37] text-[#050a09] font-semibold'
                    : 'bg-[#0b1412] text-[#A3A3A3] hover:text-[#E8E6D9] border border-white/10'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Upcoming Events Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>

          {/* Note */}
          <div className="mt-12 text-center">
            <p className="text-[#525252] text-sm font-['Manrope']">
              Schedule subject to change. Follow us on{' '}
              <a 
                href="https://instagram.com/theshamrockbern" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#D4AF37] hover:underline"
              >
                @theshamrockbern
              </a>
              {' '}for updates.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1B4D3E]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#E8E6D9] mb-4">
            Don&apos;t Miss <span className="text-[#D4AF37]">the Action!</span>
          </h2>
          <p className="text-[#E8E6D9]/80 font-['Manrope'] mb-8">
            Book your spot for the big games and events. Best seats go fast!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/reservations"
              data-testid="events-reserve-btn"
              className="bg-[#D4AF37] text-[#050a09] px-8 py-4 rounded-sm font-bold text-sm tracking-wider uppercase hover:bg-[#E5C568] transition-all duration-300 btn-glow"
            >
              Reserve for an Event
            </Link>
            <a
              href="tel:+41337227000"
              className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 rounded-sm font-semibold text-sm tracking-wider uppercase hover:bg-[#D4AF37] hover:text-[#050a09] transition-all duration-300"
            >
              Call 033 722 70 00
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
